import { useCallback, useRef } from 'react';
import { useBeforeUnload, useBlocker, type Blocker, type BlockerFunction } from 'react-router';

/**
 * Keeps unsaved edits from being lost: leaving the page in the app (the back arrow, a link, the browser's
 * back button) waits on the admin's answer, and the browser asks before a reload or a close.
 * `allowLeave` lets the navigation that follows a create go through.
 */
export const useLeaveGuard = (changed: boolean): { blocker: Blocker; allowLeave: () => void } => {
	const allowed = useRef(false);
	const shouldBlock = useCallback<BlockerFunction>(
		({ currentLocation, nextLocation }) => changed && !allowed.current && currentLocation.pathname !== nextLocation.pathname,
		[changed]
	);
	const blocker = useBlocker(shouldBlock);
	useBeforeUnload(
		useCallback(
			(event: BeforeUnloadEvent) => {
				if (changed && !allowed.current) event.preventDefault();
			},
			[changed]
		)
	);
	const allowLeave = useCallback(() => {
		allowed.current = true;
	}, []);
	return { blocker, allowLeave };
};
